import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface FigmaFrameProps {
    /** Intrinsic width of the Figma frame in px (e.g. 1920). */
    designWidth: number;
    /** Intrinsic height of the Figma frame in px. */
    designHeight: number;
    className?: string;
    children: ReactNode;
}

/**
 * Renders an absolutely-positioned Figma frame at its native pixel coordinates,
 * then scales it proportionally to fill the available width. This keeps complex,
 * editorial layouts pixel-faithful to the design at every desktop width while the
 * section height tracks the scaled content. Used for the artistic sections that
 * cannot be cleanly expressed with flow layout.
 */
export default function FigmaFrame({ designWidth, designHeight, className, children }: FigmaFrameProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(0);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => setScale(el.clientWidth / designWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [designWidth]);

    return (
        <div
            ref={ref}
            className={className}
            style={{ position: 'relative', height: scale ? designHeight * scale : undefined, overflow: 'hidden' }}
        >
            <div
                style={{
                    width: designWidth,
                    height: designHeight,
                    transformOrigin: 'top left',
                    transform: `scale(${scale || 0})`,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    visibility: scale ? 'visible' : 'hidden',
                }}
            >
                {children}
            </div>
        </div>
    );
}
